import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { ensureExists, exists, isAssigned } from '~/utils'

import {
  type QuestionSettingsInputQuestion,
  type QuestionSettingsInputQuestionDefault,
} from '../utils'

type UpdateQuestionsMutationVariables = {
  questions: QuestionSettingsInputQuestion[]
  defaultQuestions: QuestionSettingsInputQuestionDefault[]
  surveyGroupId: string
}

const createBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_CreateQuestionBulk(
    $surveyGroupId: String!
    $input: [SurveyGroupQuestionBulkCreateItemInput!]!
  ) {
    createSurveyGroupQuestionBulk(
      surveyGroupId: $surveyGroupId
      input: $input
    ) {
      result
    }
  }
`)

const updateBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_UpdateQuestionBulk(
    $input: [SurveyGroupQuestionBulkUpdateItemInput!]!
  ) {
    updateSurveyGroupQuestionBulk(input: $input) {
      result
    }
  }
`)

const deleteBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_DeleteQuestionBulk($questionIds: [String!]!) {
    deleteSurveyGroupQuestionBulk(surveyGroupQuestionIds: $questionIds) {
      result
    }
  }
`)

const createOptionBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_CreateOptionBulk(
    $input: [SurveyGroupQuestionAnswerOptionBulkCreateItemInput!]!
  ) {
    createSurveyGroupQuestionAnswerOptionBulk(input: $input) {
      result
    }
  }
`)

const updateOptionBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_UpdateOptionBulk(
    $input: [SurveyGroupQuestionAnswerOptionBulkUpdateItemInput!]!
  ) {
    updateSurveyGroupQuestionAnswerOptionBulk(input: $input) {
      result
    }
  }
`)

const deleteOptionBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_DeleteOptionBulk($optionIds: [String!]!) {
    deleteSurveyGroupQuestionAnswerOptionBulk(
      surveyGroupQuestionAnswerOptionIds: $optionIds
    ) {
      result
    }
  }
`)

export const useUpdateQuestionsMutation = (
  options: Omit<
    UseMutationOptions<
      void,
      unknown,
      UpdateQuestionsMutationVariables,
      unknown
    >,
    'mutationFn'
  > = {},
) => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()

  return useMutation({
    mutationFn: async ({ defaultQuestions, questions, surveyGroupId }) => {
      // UIでは設問->選択肢の階層になっている。削除の順番を考えて選択肢の処理から先に行う
      // 選択肢を削除
      const optionIdsToDelete = R.differenceWith(
        defaultQuestions
          .filter((question) =>
            questions.map(R.prop('id')).includes(question.id),
          )
          .flatMap((question) => question.options),
        questions.flatMap((question) => question.options),
        (defaultOption, option) => defaultOption.id === option.id,
      ).map(R.prop('id'))
      if (0 < optionIdsToDelete.length) {
        await graphQLRequest({
          document: deleteOptionBulkDocument,
          variables: { optionIds: optionIdsToDelete },
        })
      }

      // 選択肢を作成
      const questionsForOptionsToCreate: QuestionSettingsInputQuestion[] =
        questions
          .filter(
            (question) =>
              exists(question.id) &&
              question.options.some((option) => !exists(option.id)),
          )
          .map((question) => ({
            ...question,
            options: question.options.filter((option) => !exists(option.id)),
          }))
      if (0 < questionsForOptionsToCreate.length) {
        await graphQLRequest({
          document: createOptionBulkDocument,
          variables: {
            input: questionsForOptionsToCreate.map((question) => ({
              surveyGroupQuestionId: question.id!,
              surveyAnswerOptions: question.options.map((option) => ({
                text: option.text,
                surveyOptions: R.entries(option.attachedOptionMap).map(
                  ([surveyId, option]) => ({
                    surveyId,
                    option: isAssigned(option) ? option : undefined,
                  }),
                ),
              })),
            })),
          },
        })
      }

      // 選択肢を更新
      const optionsToUpdate = R.differenceWith(
        questions
          .filter((question) => exists(question.id))
          .flatMap((question) => question.options)
          .filter((option) => exists(option.id)),
        defaultQuestions.flatMap((question) => question.options),
        (option, defaultOption) =>
          option.id === defaultOption.id &&
          option.text === defaultOption.text &&
          R.isDeepEqual(
            option.attachedOptionMap,
            defaultOption.attachedOptionMap,
          ),
      )
      if (0 < optionsToUpdate.length) {
        await graphQLRequest({
          document: updateOptionBulkDocument,
          variables: {
            input: optionsToUpdate.map((option) => ({
              surveyGroupQuestionAnswerOptionId: ensureExists(
                option.id,
                t('assertion_failed'),
              ),
              text: option.text,
              surveyOptions: R.entries(option.attachedOptionMap).map(
                ([surveyId, option]) => ({
                  surveyId,
                  option: isAssigned(option) ? option : undefined,
                }),
              ),
            })),
          },
        })
      }

      // 設問を削除
      const questionIdsToDelete = R.differenceWith(
        defaultQuestions,
        questions,
        (defaultQuestion, question) => defaultQuestion.id === question.id,
      ).map(R.prop('id'))
      if (0 < questionIdsToDelete.length) {
        await graphQLRequest({
          document: deleteBulkDocument,
          variables: { questionIds: questionIdsToDelete },
        })
      }

      // 設問を作成
      const questionsToCreate = questions.filter(
        (question) => !exists(question.id),
      )
      if (0 < questionsToCreate.length) {
        await graphQLRequest({
          document: createBulkDocument,
          variables: {
            surveyGroupId,
            input: questionsToCreate.map((question) => ({
              name: question.name,
              showCustomizedKPI: question.showCustomizedKPI,
              surveyQuestions: R.entries(question.attachedQuestionMap)
                .map(([surveyId, questionId]) => ({ surveyId, questionId }))
                .filter(
                  (
                    surveyQuestion,
                  ): surveyQuestion is {
                    surveyId: string
                    questionId: string
                  } => isAssigned(surveyQuestion.questionId),
                ),
              surveyAnswerOptions: question.options.map((option) => ({
                text: option.text,
                surveyOptions: R.entries(option.attachedOptionMap).map(
                  ([surveyId, option]) => ({
                    surveyId,
                    option: isAssigned(option) ? option : undefined,
                  }),
                ),
              })),
            })),
          },
        })
      }

      // 設問を更新
      const questionsToUpdate = R.differenceWith(
        questions.filter((question) => exists(question.id)),
        defaultQuestions,
        (question, defaultQuestion) =>
          question.id === defaultQuestion.id &&
          question.name === defaultQuestion.name &&
          question.showCustomizedKPI === defaultQuestion.showCustomizedKPI &&
          R.isDeepEqual(
            question.attachedQuestionMap,
            defaultQuestion.attachedQuestionMap,
          ),
      )
      if (0 < questionsToUpdate.length) {
        await graphQLRequest({
          document: updateBulkDocument,
          variables: {
            input: questionsToUpdate.map((question) => ({
              surveyGroupQuestionId: question.id!,
              name: question.name,
              showCustomizedKPI: question.showCustomizedKPI,
              surveyQuestions: R.entries(question.attachedQuestionMap)
                .map(([surveyId, questionId]) => ({ surveyId, questionId }))
                .filter(
                  (
                    surveyQuestion,
                  ): surveyQuestion is {
                    surveyId: string
                    questionId: string
                  } => isAssigned(surveyQuestion.questionId),
                ),
            })),
          },
        })
      }
    },
    ...options,
  })
}
